/**
 * Reset password (A-05 staff, A-03/A-04 doctors) — shown in a modal from the admin's Staff and
 * Doctors lists. The admin sets a password for someone who can't get in and passes it on; the API
 * signs them out everywhere and asks them to change it after signing in. The password is checked
 * against the rule the API publishes (usePasswordRules, AUTH-15) and typed twice, since the admin
 * has to hand it over exactly.
 */

import { useState } from "react";
import type { ReactNode } from "react";

import { usePasswordRules } from "../api/usePasswordRules";
import { passesAll } from "../lib/passwordRules";
import { PasswordChecklist } from "./ui/PasswordChecklist";
import { PasswordInput } from "./ui/PasswordInput";

export function ResetPasswordForm({
  name,
  note,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  /** Whose password it is. */
  name: string;
  /** Why the password won't work yet, when it won't (e.g. their login is off). */
  note?: ReactNode;
  submitting: boolean;
  error: string | null;
  onSubmit: (newPassword: string) => void;
  onCancel: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const passwordRules = usePasswordRules();
  const input = "w-full rounded-lg border px-3 py-1.5 outline-hidden focus:border-brand focus:ring-1 focus:ring-brand";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!passesAll(passwordRules, password)) {
          setLocalError("The new password doesn't meet the rules below yet.");
          return;
        }
        if (password !== confirm) {
          setLocalError("Passwords don’t match.");
          return;
        }
        setLocalError(null);
        onSubmit(password);
      }}
      className="space-y-4"
    >
      <p className="text-sm text-gray-500">
        Set a new password for <span className="font-medium text-gray-700">{name}</span> and share it
        with them privately. They’ll be signed out everywhere and asked to change it after signing in.
      </p>
      {note && <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{note}</p>}
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">
          New password
          <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>
        </span>
        <PasswordInput
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className={input}
        />
      </label>
      <PasswordChecklist password={password} rules={passwordRules} />
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">
          Confirm password
          <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>
        </span>
        <PasswordInput
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          className={input}
        />
      </label>
      {(localError ?? error) && <div className="text-sm text-red-600">{localError ?? error}</div>}
      <div className="flex gap-2">
        <button className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50" disabled={submitting}>
          {submitting ? "Resetting…" : "Reset password"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border px-4 py-2 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}
